import React, { useState } from "react";
import {
  CheckCircle2,
  Star,
  Leaf,
  Zap,
  Droplets,
  Wheat,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

// IMPORT IMAGES (Ensure these exist in src/assets/products/)
import moringaImg from "../assets/moringa.jpeg";
import bananaImg from "../assets/banana.jpeg";
import beetrootImg from "../assets/beetroot.jpeg";
import tomatoImg from "../assets/tomato.jpeg";
import gingerImg from "../assets/ginger.jpeg";
import garlicImg from "../assets/garlic.jpeg";
import onionImg from "../assets/onion.jpeg";
import chiliImg from "../assets/chili.jpeg";

const Products = () => {
  const [expandedIndices, setExpandedIndices] = useState({});

  const toggleCategory = (idx) => {
    setExpandedIndices((prev) => ({
      ...prev,
      [idx]: !prev[idx],
    }));
  };

  // 1. EXISTING TEXT CATALOG
  const catalog = [
    {
      category: "Dehydrated Fruits",
      icon: <Star className="text-accent" />,
      items: [
        "Mango (Slices, Dices, & Powder)",
        "Banana",
        "Pineapple",
        "Orange",
        "Papaya (Green/Orange)",
        "Apple",
        "Taro",
      ],
    },
    {
      category: "Dehydrated Vegetables",
      icon: <Leaf className="text-accent" />,
      items: [
        "Onion",
        "Carrot",
        "Beetroot",
        "Lemon",
        "Tomato",
        "Moringa",
        "Curry Leaves",
        "Spinach Powder",
        "Potato (French Fries)",
      ],
    },
    {
      category: "Spice Powders",
      icon: <Zap className="text-accent" />,
      items: [
        "Chilli Powder (Flakes)",
        "Turmeric",
        "Cumin",
        "Coriander",
        "Fenugreek Powder",
        "Ginger & Garlic",
      ],
    },
    {
      category: "Millets & Flours",
      icon: <Wheat className="text-accent" />,
      items: ["Ragi", "Jowar", "Bajra Flour"],
    },
    {
      category: "Coconut Specialties",
      icon: <Droplets className="text-accent" />,
      items: ["Premium Coconut Products"],
    },
  ];

  // 2. NEW GALLERY DATA
  const galleryImages = [
    { src: moringaImg, name: "Moringa Powder" },
    { src: chiliImg, name: "Chilli Powder" },
    { src: bananaImg, name: "Banana Powder" },
    { src: tomatoImg, name: "Tomato Powder" },
    { src: onionImg, name: "White Onion Powder" },
    { src: gingerImg, name: "Ginger Powder" },
    { src: garlicImg, name: "Garlic Powder" },
    { src: beetrootImg, name: "Beetroot Powder" },
  ];

  return (
    <section
      id="products"
      className="py-16 md:py-24 bg-white scroll-mt-16 font-sans"
    >
      <div className="container mx-auto px-4">
        {/* SECTION HEADER */}
        <div className="text-center mb-16">
          <h2 className="font-display text-3xl md:text-5xl font-bold text-primary mb-4 tracking-tight uppercase">
            Our Products
          </h2>
          <div className="w-16 h-1.5 bg-accent mx-auto mb-6"></div>
          <p className="text-slate-600 max-w-2xl mx-auto text-lg">
            From farm to factory to your doorstep, we ensure every grain retains
            its natural essence.
          </p>
        </div>

        {/* PART 1: TEXT CATALOG GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-start mb-24">
          {catalog.map((cat, idx) => {
            const isExpanded = expandedIndices[idx];
            const visibleItems = isExpanded ? cat.items : cat.items.slice(0, 4);
            const hasMore = cat.items.length > 4;

            return (
              <div
                key={idx}
                className={`bg-slate-50 p-8 rounded-3xl border transition-all duration-300 group flex flex-col h-full ${
                  isExpanded
                    ? "border-accent shadow-xl ring-1 ring-accent/20"
                    : "border-slate-100 hover:shadow-lg"
                }`}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="h-12 w-12 bg-primary rounded-xl flex items-center justify-center shrink-0">
                    {React.cloneElement(cat.icon, { size: 24 })}
                  </div>
                  <h3 className="font-display text-xl font-black text-primary uppercase leading-tight">
                    {cat.category}
                  </h3>
                </div>

                <div className="flex-grow">
                  <ul className="space-y-3 transition-all duration-500 ease-in-out">
                    {visibleItems.map((item, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-slate-700 font-medium animate-in fade-in slide-in-from-top-1"
                      >
                        <CheckCircle2
                          size={18}
                          className="text-accent shrink-0 mt-0.5"
                        />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {hasMore && (
                  <button
                    onClick={() => toggleCategory(idx)}
                    className="mt-6 flex items-center gap-2 text-primary font-bold text-sm uppercase tracking-wider hover:text-accent transition-colors self-start"
                  >
                    {isExpanded ? (
                      <>
                        <ChevronUp size={20} /> Show Less
                      </>
                    ) : (
                      <>
                        <ChevronDown size={20} /> View {cat.items.length - 4}{" "}
                        More
                      </>
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* PART 2: VISUAL GALLERY (NEW SECTION) */}
        <div className="bg-primary rounded-[2.5rem] p-6 sm:p-8 md:p-12 overflow-hidden relative">
          {/* Decorative Background Element */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-accent/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none"></div>

          <div className="text-center mb-10 relative z-10">
            <h3 className="text-2xl md:text-4xl font-black text-white uppercase tracking-tight mb-3">
              Visual Showcase of our products
            </h3>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-8 md:gap-8 relative z-10">
            {galleryImages.map((img, index) => (
              <div
                key={index}
                className="flex flex-col items-center gap-3 group"
              >
                {/* Image Container */}
                <div className="relative rounded-full overflow-hidden aspect-square border-4 border-accent shadow-2xl w-24 sm:w-32 md:w-40">
                  <img
                    src={img.src}
                    alt={img.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  {/* Subtle Hover Overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300"></div>
                </div>

                {/* Label Underneath */}
                <p className="text-white font-bold text-xs sm:text-sm md:text-base text-center uppercase tracking-wider leading-tight">
                  {img.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Products;
